<script>
export default {
    name: 'BaseCalendarNavigation',
};
</script>

<script setup>
import { computed, ref } from 'vue';
import formatDate from '@/utils/format-date.ts';
import { VIEW } from '@/components/base-components/components/calendar/index.js';
import { areDatesEqual } from '@/components/base-components/components/date-picker/utils/helpers.js';
import BaseCalendarNavigationPeriodPopover from '@/components/base-components/components/calendar/components/navigation/period/popover.vue';
import { shiftMonthSafe, shiftYearSafe } from '@/components/base-components/components/calendar/utils/shift.js';

const props = defineProps({
    value: {
        type: Date,
    },
    view: {
        type: String,
        default: VIEW.MONTH,
        validator: value => Object.values(VIEW).includes(value),
    },
    firstDayOfWeek: {
        type: Number,
        default: 1,
    },
    noToday: {
        type: Boolean,
    },
    formatterOptions: {
        type: Object,
    },
    disabled: {
        type: Boolean,
    },
});

const emit = defineEmits({
    input: null,
});

const model = computed({
    get: () => props.value ?? new Date(),
    set: value => emit('input', value),
});

const periodLabel = computed(() => {
    const { day, month, year } = {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        ...props.formatterOptions,
    };

    const options = {
        [VIEW.DAY]: { day, month, year },
        [VIEW.WEEK]: { month, year },
        [VIEW.MONTH]: { month, year },
        [VIEW.YEAR]: { year },
        [VIEW.SCHEDULE]: { month, year },
    }[props.view];

    return formatDate(model.value.toString(), options);
});

const today = ref(new Date());

function goTo(date) {
    if (props.disabled) return;

    model.value = date;

    today.value = new Date();
}

function goToToday() {
    goTo(today.value);
}

function goToPrevious() {
    const current = new Date(model.value);

    const nextDate = {
        [VIEW.DAY]: new Date(current.getFullYear(), current.getMonth(), current.getDate() - 1),
        [VIEW.WEEK]: new Date(current.getFullYear(), current.getMonth(), current.getDate() - 7),
        [VIEW.MONTH]: shiftMonthSafe(current, -1),
        [VIEW.YEAR]: shiftYearSafe(current, -1),
        [VIEW.SCHEDULE]: shiftMonthSafe(current, -1),
    }[props.view];

    goTo(nextDate);
}

function goToNext() {
    const current = new Date(model.value);

    const nextDate = {
        [VIEW.DAY]: new Date(current.getFullYear(), current.getMonth(), current.getDate() + 1),
        [VIEW.WEEK]: new Date(current.getFullYear(), current.getMonth(), current.getDate() + 7),
        [VIEW.MONTH]: shiftMonthSafe(current, 1),
        [VIEW.YEAR]: shiftYearSafe(current, 1),
        [VIEW.SCHEDULE]: shiftMonthSafe(current, 1),
    }[props.view];

    goTo(nextDate);
}
</script>

<template>
    <div class="flex items-center gap-x-2">
        <slot
            :date="model"
            :period-label="periodLabel"
            :go-to="goTo"
            :go-to-today="goToToday"
            :go-to-previous="goToPrevious"
            :go-to-next="goToNext"
        >
            <div class="flex flex-1 items-center gap-x-1">
                <div class="flex items-center">
                    <base-button
                        :icon="{
                            name: 'chevron-left'
                        }"
                        :disabled="disabled"
                        size="small"
                        custom-size-class="px-2 py-1.5"
                        text
                        @click="goToPrevious"
                    />
                    <base-button
                        :icon="{
                            name: 'chevron-right'
                        }"
                        :disabled="disabled"
                        size="small"
                        custom-size-class="px-2 py-1.5"
                        text
                        @click="goToNext"
                    />
                </div>

                <div class="flex flex-1 items-center justify-center">
                    <base-calendar-navigation-period-popover
                        v-model="model"
                        :label="periodLabel"
                        :view="view"
                        :first-day-of-week="firstDayOfWeek"
                        :formatter-options="formatterOptions"
                        :disabled="disabled"
                    />
                </div>
            </div>

            <base-button
                v-if="!noToday"
                :disabled="areDatesEqual(model, today, view)"
                label="Today"
                size="small"
                text
                @click="goToToday"
            />
        </slot>
    </div>
</template>
